import { useEffect, useRef, useState } from 'react';
import { Backdrop, Box, Button, CircularProgress, Fade, Modal } from '@material-ui/core';
import DailyLimitPanel from './DailyLimitPanel';
import { resolveDailyLimit } from './daily-limit';
import { PLAN_WAIT_MS, readStorage, useSelectedPlans } from './use-daily-limit';

const TITLE_ID = 'daily-limit-modal-title';

/**
 * Create Quiz's out-of-questions modal (it replaced the "Attention / You've run out
 * of questions" modal). Create Quiz opens it when the count says
 * questionLeftInDay <= 0 for anyone but a tutor or admin, and that is its only
 * opener. The words come from DailyLimitPanel:
 * - a confirmed free plan: "Today's N free questions: done ✓", when the next N
 *   unlock, and the Pro trial when the student is eligible;
 * - any other plan, or an unknown one: the same without "free" and without a trial;
 * - a plan with no daily questions, or a count without a limit: their own lines.
 *
 * Props: open, onClose, onSeePlans, onShare (Create Quiz's share window), count
 * (the quesCount response), exam (the selected exam when there is exactly one).
 */
export default function DailyLimitModal({ open, onClose, onSeePlans, onShare, count, exam = null }) {
  const userId = readStorage('userId');
  const { plans, settled } = useSelectedPlans({ enabled: open, userId, fetchIfMissing: true });
  // Lives as long as Create Quiz: the modal re-opens on every count response
  // (each filter change), and that is still one view of the limit moment.
  const shownRef = useRef(false);

  const [planWaitOver, setPlanWaitOver] = useState(false);
  useEffect(() => {
    setPlanWaitOver(false);
    if (!open || settled) return undefined;
    const timer = setTimeout(() => setPlanWaitOver(true), PLAN_WAIT_MS);
    return () => clearTimeout(timer);
  }, [open, settled]);

  // The modal opens on questionLeftInDay <= 0, which (for a malformed count)
  // includes null; that still reads as "none left, limit unknown".
  const info = resolveDailyLimit({ count, plans, exam })
    || { left: 0, limit: null, exhausted: true, free: plans ? false : null, exam: null };
  const ready = settled || planWaitOver || !userId;

  return (
    <Modal
      className="tutor-mode-confirm-modal sub-end-modal daily-limit-modal"
      open={open}
      onClose={onClose}
      closeAfterTransition
      BackdropComponent={Backdrop}
      BackdropProps={{ timeout: 500 }}
      aria-labelledby={TITLE_ID}
    >
      <Fade in={open}>
        <Box className="modal-container" data-testid="daily-limit-modal">
          {ready ? (
            <DailyLimitPanel
              variant="modal"
              surface="create_quiz"
              info={info}
              onSeePlans={onSeePlans}
              onShare={onShare}
              onClose={onClose}
              titleId={TITLE_ID}
              shownRef={shownRef}
            />
          ) : (
            <Box className="daily-limit-actions daily-limit-pending">
              <Box role="status" aria-label="Loading" className="daily-limit-spinner">
                <CircularProgress size={24} aria-hidden="true" />
              </Box>
              {/* Never trap the student behind a slow read. */}
              <Button variant="text" className="daily-limit-close" onClick={onClose}>Close</Button>
            </Box>
          )}
        </Box>
      </Fade>
    </Modal>
  );
}
